import type { GroupedUsersList } from '@halo/ui-chat-react';
import { StateEnum, useAppStore } from '@halo/chat-sdk-react';
import { jsx } from 'slate-hyperscript';
import { createCustomEditor, createLinkElement, serializePayload } from '@halo/ui-chat-react';

export const useSearch = (searchValue: string, resetTrigger: number, isOpen: boolean) => {
  const { t } = useTranslation();
  const value = useDeferredValue(searchValue);
  const [contactsUsers, setContactsUsers] = useState<GroupedUsersList[]>([]);
  const [sessionUsers, setSessionUsers] = useState<GroupedUsersList[]>([]);
  const [globalUsers, setGlobalUsers] = useState<GroupedUsersList[]>([]);
  const { client } = useAppStore();

  const list = useMemo(() => {
    return [...contactsUsers, ...sessionUsers, ...globalUsers];
  }, [contactsUsers, sessionUsers, globalUsers]);

  const { run: onSearch } = useDebounceFn(
    async () => {
      const { users: contactsRes } =
        (await client?.Api.contacts.contactsSearchByKeyInLocal(value)) ?? {};
      const filteredContacts = contactsRes?.filter(
        (item) => item.state !== StateEnum.DELETE_ACCOUNT
      );
      setContactsUsers([{ label: t('chat.membersInContacts'), items: filteredContacts ?? [] }]);

      const sessionRes = await client?.Api.session.sessionSearchByKey(value, true);
      const filteredSession = sessionRes?.filter(
        (item) =>
          !filteredContacts?.some((contact) => contact.user_id === item.user_id) &&
          item.state !== StateEnum.DELETE_ACCOUNT
      );
      setSessionUsers([{ label: t('chat.membersInChats'), items: filteredSession ?? [] }]);

      const globalRes = await client?.Api.contacts.contactsSearchByBeyInGlobal(value);
      const filteredGlobal = globalRes?.filter((item) => {
        return (
          !filteredSession?.some((session) => session.user_id === item.user_id) &&
          !filteredContacts?.some((contact) => contact.user_id === item.user_id)
        );
      });

      setGlobalUsers([
        {
          label: t('chat.membersInGlobal'),
          items: filteredGlobal ?? [],
        },
      ]);
    },
    { wait: 300 }
  );

  useEffect(() => {
    if (isOpen) {
      onSearch();
    }
  }, [value, onSearch, isOpen]);

  useEffect(() => {
    if (resetTrigger !== undefined) {
      setContactsUsers([]);
      setSessionUsers([]);
      setGlobalUsers([]);
    }
  }, [resetTrigger]);

  return { onSearch, contactsUsers, sessionUsers, globalUsers, list };
};

export const genInviteMessage = (text: string, link: string) => {
  const editor = createCustomEditor({
    children: jsx('fragment', [jsx('element', text), jsx('element', [createLinkElement(link)])]),
  });

  const result = serializePayload.serialize(editor);

  return result.payload;
};
